'use client';

import { useCallback, useState } from 'react';

import {
  calculatePoints,
  getBoostTip,
  getScoreLabel,
  MAX_POINTS,
  QUALIFY_THRESHOLD,
  type AgeGroup,
  type EnglishLevel,
  type ExperienceLevel,
  type GermanLevel,
  type QualificationLevel,
} from './chancenkarte-logic';

import styles from './ChancenkarteCalculator.module.css';

/* ─── Step definitions ──────────────────────────────────────────────── */
type StepId = 'qualification' | 'german' | 'english' | 'experience' | 'age' | 'germanyStay';

interface Option<T extends string | boolean> {
  value: T;
  label: string;
  sublabel?: string;
  points: number;
}

interface Step<T extends string | boolean = string | boolean> {
  id: StepId;
  stepNumber: number;
  icon: string;
  question: string;
  options: Option<T>[];
}

const STEPS: Step[] = [
  {
    id: 'qualification',
    stepNumber: 1,
    icon: 'school',
    question: 'What is your highest professional qualification?',
    options: [
      { value: 'recognized_degree' as QualificationLevel, label: 'University degree — recognised in Germany', sublabel: 'Or EU/Swiss equivalent', points: 4 },
      { value: 'unrecognized_degree' as QualificationLevel, label: 'University degree — not yet recognised', sublabel: 'Foreign credential pending evaluation', points: 2 },
      { value: 'vocational' as QualificationLevel, label: 'Vocational / trade qualification', sublabel: 'Completed apprenticeship or trade cert', points: 2 },
      { value: 'none' as QualificationLevel, label: 'No recognised qualification', sublabel: '', points: 0 },
    ] as Option<QualificationLevel>[],
  },
  {
    id: 'german',
    stepNumber: 2,
    icon: 'translate',
    question: 'What is your German language level?',
    options: [
      { value: 'c1_plus' as GermanLevel, label: 'C1 or above', sublabel: 'Advanced — near native fluency', points: 3 },
      { value: 'b2' as GermanLevel, label: 'B2', sublabel: 'Upper intermediate', points: 2 },
      { value: 'b1' as GermanLevel, label: 'B1', sublabel: 'Intermediate', points: 1 },
      { value: 'a2' as GermanLevel, label: 'A2 or below', sublabel: 'Beginner', points: 0 },
      { value: 'none' as GermanLevel, label: 'No German', sublabel: '', points: 0 },
    ] as Option<GermanLevel>[],
  },
  {
    id: 'english',
    stepNumber: 3,
    icon: 'language',
    question: 'What is your English language level?',
    options: [
      { value: 'b2_plus' as EnglishLevel, label: 'B2 or above', sublabel: 'Upper intermediate to advanced', points: 1 },
      { value: 'a1_b1' as EnglishLevel, label: 'Below B2', sublabel: 'A1 through B1', points: 0 },
      { value: 'none' as EnglishLevel, label: 'No English', sublabel: '', points: 0 },
    ] as Option<EnglishLevel>[],
  },
  {
    id: 'experience',
    stepNumber: 4,
    icon: 'work_history',
    question: 'How many years of skilled work experience do you have?',
    options: [
      { value: 'y5_plus' as ExperienceLevel, label: '5 years or more', sublabel: 'In your professional field', points: 2 },
      { value: 'y3_5' as ExperienceLevel, label: '3 – 5 years', sublabel: 'In your professional field', points: 1 },
      { value: 'lt3' as ExperienceLevel, label: 'Less than 3 years', sublabel: '', points: 0 },
      { value: 'none' as ExperienceLevel, label: 'No work experience', sublabel: '', points: 0 },
    ] as Option<ExperienceLevel>[],
  },
  {
    id: 'age',
    stepNumber: 5,
    icon: 'cake',
    question: 'How old are you?',
    options: [
      { value: 'under35' as AgeGroup, label: 'Under 35', sublabel: '', points: 2 },
      { value: 'y35_40' as AgeGroup, label: '35 – 40', sublabel: '', points: 1 },
      { value: 'over40' as AgeGroup, label: 'Over 40', sublabel: '', points: 0 },
    ] as Option<AgeGroup>[],
  },
  {
    id: 'germanyStay',
    stepNumber: 6,
    icon: 'location_city',
    question: 'Have you previously lived or worked in Germany for 6+ months?',
    options: [
      { value: true, label: 'Yes', sublabel: 'Study, work, or residence of 6+ months', points: 1 },
      { value: false, label: 'No', sublabel: '', points: 0 },
    ] as Option<boolean>[],
  },
];

/* ─── Default (unset) state ─────────────────────────────────────────── */
interface Selections {
  qualification: QualificationLevel | null;
  german: GermanLevel | null;
  english: EnglishLevel | null;
  experience: ExperienceLevel | null;
  age: AgeGroup | null;
  germanyStay: boolean | null;
}

const DEFAULT_SELECTIONS: Selections = {
  qualification: null,
  german: null,
  english: null,
  experience: null,
  age: null,
  germanyStay: null,
};

/* ─── Component ─────────────────────────────────────────────────────── */
export function ChancenkarteCalculator() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<Selections>(DEFAULT_SELECTIONS);
  const [showResult, setShowResult] = useState(false);
  const [animatingPoint, setAnimatingPoint] = useState<number | null>(null);
  const [runningTotal, setRunningTotal] = useState(0);

  const totalSteps = STEPS.length;
  const step = STEPS[currentStep]!;

  const selectOption = useCallback(
    (value: string | boolean, points: number) => {
      const newSelections = { ...selections, [step.id]: value };
      setSelections(newSelections);

      // Animate points
      if (points > 0) {
        setAnimatingPoint(points);
        setTimeout(() => setAnimatingPoint(null), 900);
      }

      // Update running total from all answered questions
      const tempTotal = STEPS.slice(0, currentStep + 1).reduce((acc, s) => {
        const val = s.id === step.id ? value : selections[s.id as keyof Selections];
        if (val === null) return acc;
        const opt = s.options.find((o: Option<string | boolean>) => o.value === val);
        return acc + (opt?.points ?? 0);
      }, 0);
      setRunningTotal(tempTotal);

      // Advance to next step after short delay
      setTimeout(() => {
        if (currentStep < totalSteps - 1) {
          setCurrentStep((prev) => prev + 1);
        } else {
          setShowResult(true);
        }
      }, 340);
    },
    [currentStep, selections, step, totalSteps],
  );

  const reset = () => {
    setSelections(DEFAULT_SELECTIONS);
    setCurrentStep(0);
    setShowResult(false);
    setRunningTotal(0);
    setAnimatingPoint(null);
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  // Full result calculation
  const breakdown =
    showResult && Object.values(selections).every((v) => v !== null)
      ? calculatePoints({
          qualification: selections.qualification!,
          germanLevel: selections.german!,
          englishLevel: selections.english!,
          experience: selections.experience!,
          age: selections.age!,
          previousGermanyStay: selections.germanyStay!,
        })
      : null;

  const scoreLabel = breakdown ? getScoreLabel(breakdown.total) : null;
  const boostTip = breakdown
    ? getBoostTip(
        {
          qualification: selections.qualification!,
          germanLevel: selections.german!,
          englishLevel: selections.english!,
          experience: selections.experience!,
          age: selections.age!,
          previousGermanyStay: selections.germanyStay!,
        },
        breakdown,
      )
    : null;

  const progressPct = ((currentStep + (showResult ? 1 : 0)) / totalSteps) * 100;

  /* ─── Result screen ─────────────────────────────────────────────── */
  if (showResult && breakdown && scoreLabel) {
    const isQualifying = scoreLabel.tier === 'qualifying';
    const pct = Math.round((breakdown.total / MAX_POINTS) * 100);

    return (
      <div className={styles.calculator}>
        <div className={`${styles.resultCard} ${styles[`tier_${scoreLabel.tier}`]}`}>
          {/* Score gauge */}
          <div className={styles.gaugeWrap} aria-label={`Score: ${breakdown.total} out of ${MAX_POINTS} points`}>
            <svg className={styles.gaugeSvg} viewBox="0 0 120 120" aria-hidden="true">
              <circle cx="60" cy="60" r="50" className={styles.gaugeTrack} />
              <circle
                cx="60"
                cy="60"
                r="50"
                className={`${styles.gaugeBar} ${styles[`gaugeBar_${scoreLabel.tier}`]}`}
                style={{ strokeDashoffset: `${314 - (314 * pct) / 100}` }}
              />
            </svg>
            <div className={styles.gaugeLabel}>
              <span className={styles.gaugeScore}>{breakdown.total}</span>
              <span className={styles.gaugeOf}>/ {MAX_POINTS} pts</span>
            </div>
          </div>

          {/* Verdict badge */}
          <div className={`${styles.verdictBadge} ${styles[`verdict_${scoreLabel.tier}`]}`}>
            <span className="material-icons" aria-hidden="true">
              {isQualifying ? 'check_circle' : scoreLabel.tier === 'borderline' ? 'info' : 'cancel'}
            </span>
            {scoreLabel.label}
          </div>

          <p className={styles.thresholdNote}>
            The Chancenkarte requires <strong>{QUALIFY_THRESHOLD} points</strong> to qualify.{' '}
            {isQualifying ? (
              <>You scored <strong>{breakdown.total}</strong> — you qualify! 🎉</>
            ) : (
              <>You scored <strong>{breakdown.total}</strong> — {QUALIFY_THRESHOLD - breakdown.total} more point{QUALIFY_THRESHOLD - breakdown.total > 1 ? 's' : ''} needed.</>
            )}
          </p>

          {/* Breakdown table */}
          <div className={styles.breakdown}>
            <h3 className={styles.breakdownTitle}>Your point breakdown</h3>
            {[
              { label: 'Professional qualification', pts: breakdown.qualification, max: 4 },
              { label: 'German language', pts: breakdown.german, max: 3 },
              { label: 'English language', pts: breakdown.english, max: 1 },
              { label: 'Work experience', pts: breakdown.experience, max: 2 },
              { label: 'Age bonus', pts: breakdown.age, max: 2 },
              { label: 'Previous stay in Germany', pts: breakdown.germanyStay, max: 1 },
            ].map(({ label, pts, max }) => (
              <div key={label} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>{label}</span>
                <div className={styles.breakdownBar}>
                  <div
                    className={`${styles.breakdownFill} ${pts > 0 ? styles.fillActive : styles.fillZero}`}
                    style={{ width: `${(pts / max) * 100}%` }}
                  />
                </div>
                <span className={styles.breakdownPts}>
                  {pts}/{max}
                </span>
              </div>
            ))}
          </div>

          {/* Boost tip */}
          {boostTip && (
            <div className={styles.boostTip}>
              <span className="material-icons" aria-hidden="true">tips_and_updates</span>
              <p>{boostTip}</p>
            </div>
          )}

          {/* CTAs */}
          <div className={styles.resultCtas}>
            <a href="/germany-cv-builder" className={styles.ctaPrimary}>
              <span className="material-icons" aria-hidden="true">description</span>
              Build my German Lebenslauf
            </a>
            <button onClick={reset} className={styles.ctaSecondary} type="button">
              <span className="material-icons" aria-hidden="true">refresh</span>
              Recalculate
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Question screen ───────────────────────────────────────────── */
  return (
    <div className={styles.calculator}>
      {/* Progress bar + running total */}
      <div className={styles.progressHeader}>
        <div className={styles.progressMeta}>
          <span className={styles.stepLabel}>
            Step {currentStep + 1} of {totalSteps}
          </span>
          <div className={styles.runningTotal}>
            <span className="material-icons" aria-hidden="true">stars</span>
            <span>{runningTotal} pts so far</span>
            {animatingPoint !== null && (
              <span className={styles.pointsChip} key={Date.now()}>+{animatingPoint}</span>
            )}
          </div>
        </div>
        <div className={styles.progressTrack} role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Step card */}
      <div className={styles.stepCard} key={step.id}>
        <div className={styles.stepIcon} aria-hidden="true">
          <span className="material-icons">{step.icon}</span>
        </div>
        <h2 className={styles.stepQuestion}>{step.question}</h2>

        <div className={styles.options}>
          {(step.options as Option<string | boolean>[]).map((opt) => (
            <button
              key={String(opt.value)}
              className={`${styles.optionBtn} ${selections[step.id as keyof Selections] === opt.value ? styles.optionSelected : ''}`}
              onClick={() => selectOption(opt.value, opt.points)}
              type="button"
              aria-pressed={selections[step.id as keyof Selections] === opt.value}
            >
              <div className={styles.optionText}>
                <span className={styles.optionLabel}>{opt.label}</span>
                {opt.sublabel && <span className={styles.optionSublabel}>{opt.sublabel}</span>}
              </div>
              {opt.points > 0 && (
                <span className={styles.optionPoints}>+{opt.points}</span>
              )}
            </button>
          ))}
        </div>

        {currentStep > 0 && (
          <button onClick={goBack} className={styles.backBtn} type="button">
            <span className="material-icons" aria-hidden="true">arrow_back</span>
            Back
          </button>
        )}
      </div>
    </div>
  );
}
